// Global privacy mode — persisted per-device via localStorage, applies across
// every route. Supports a one-tap temporary reveal that auto-locks after a
// configurable window. Subscribers get notified on any state change.

const KEY = "sophia:privacy:on";
const REVEAL_MS_KEY = "sophia:privacy:reveal-ms";
const DEFAULT_REVEAL_MS = 30_000;

let on = false;
let revealUntil = 0;
let revealTimer: number | null = null;

try {
  if (typeof localStorage !== "undefined") {
    on = localStorage.getItem(KEY) === "1";
  }
} catch { /* noop */ }

const listeners = new Set<(s: PrivacyState) => void>();

export interface PrivacyState {
  on: boolean;
  revealed: boolean;
  revealMsLeft: number;
}

function state(): PrivacyState {
  const now = Date.now();
  const revealed = revealUntil > now;
  return { on, revealed, revealMsLeft: revealed ? revealUntil - now : 0 };
}
function emit() { const s = state(); listeners.forEach((l) => l(s)); }

export function isPrivate(): boolean {
  return on && Date.now() >= revealUntil;
}
export function getPrivacyState(): PrivacyState { return state(); }

export function setPrivacyMode(next: boolean) {
  on = !!next;
  try { localStorage.setItem(KEY, on ? "1" : "0"); } catch { /* noop */ }
  if (!on) { revealUntil = 0; if (revealTimer) { clearTimeout(revealTimer); revealTimer = null; } }
  emit();
}
export function togglePrivacyMode() { setPrivacyMode(!on); }

export function revealFor(ms?: number) {
  if (!on) return;
  const dur = ms ?? getRevealMs();
  revealUntil = Date.now() + dur;
  if (revealTimer) clearTimeout(revealTimer);
  revealTimer = window.setTimeout(() => { revealUntil = 0; revealTimer = null; emit(); }, dur + 40);
  emit();
}
export function lockNow() {
  revealUntil = 0;
  if (revealTimer) { clearTimeout(revealTimer); revealTimer = null; }
  emit();
}

export function getRevealMs(): number {
  try {
    const v = Number(localStorage.getItem(REVEAL_MS_KEY) ?? DEFAULT_REVEAL_MS);
    return Number.isFinite(v) && v > 1000 ? v : DEFAULT_REVEAL_MS;
  } catch { return DEFAULT_REVEAL_MS; }
}
export function setRevealMs(ms: number) {
  try { localStorage.setItem(REVEAL_MS_KEY, String(Math.max(2000, ms | 0))); } catch { /* noop */ }
  emit();
}

export function subscribePrivacy(fn: (s: PrivacyState) => void) {
  listeners.add(fn); fn(state()); return () => { listeners.delete(fn); };
}

/** Mask helper — obscures text when private mode is active and not revealed. */
export function maskText(text: string, opts?: { keepChars?: number }): string {
  if (!isPrivate()) return text;
  const keep = opts?.keepChars ?? 0;
  if (!text) return text;
  if (keep > 0 && text.length > keep * 2) {
    return text.slice(0, keep) + " •••••• " + text.slice(-keep);
  }
  return "•••••• (hidden)";
}

import { useEffect, useState } from "react";
export function usePrivacy(): PrivacyState {
  const [s, setS] = useState<PrivacyState>(() => state());
  useEffect(() => subscribePrivacy(setS), []);
  return s;
}
